import { publicKeys } from '@/api/query-keys';
import { ArrowRight, Gift, Percent } from 'lucide-react';
import { useEffect } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/ui/link';
import { Section, Title } from '@/components/ui/layout';
import { LoadingState } from '@/components/ui/loading';
import { OrganizationProjectCard } from '@/components/organization-project-card';
import type { components } from '@/api/generated-types';
import { useLanguage } from '@/i18n/language-context';
import { projectDonationHref } from '@/lib/links';
import { churchPaths, useChurchHierarchy } from './church-data';
import { useChurchCopy } from './church-copy';
import { useGiving } from './giving';
import { publicRequest as send } from '@/api/public-client';
import { isChurchGivingFund, readGivingFund } from './giving-funds';
import './home-projects.css';

type Catalog = components['schemas']['PublicProjectsPageResponseDto'];
function QuickGiving({
  churchId,
  conferenceId,
}: {
  churchId: string;
  conferenceId: string;
}): JSX.Element | null {
  const { locale } = useLanguage();
  const copy = useChurchCopy();
  const targets = [
    { kind: 'tithe' as const, label: copy.tithe, icon: Percent },
    { kind: 'general-fund' as const, label: copy.localBudget, icon: Gift },
  ];
  const funds = useQueries({
    queries: targets.map(({ kind }) => ({
      queryKey: publicKeys.churchFund(churchId, kind, locale),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        readGivingFund(churchId, kind, locale, signal),
      retry: 1,
    })),
  });
  const available = funds.flatMap((query, index) => {
    const fund = query.data;
    const target = targets[index];
    if (!target) return [];
    if (!isChurchGivingFund(fund, target.kind, churchId, conferenceId)) return [];
    return [{ ...target, fund }];
  });
  if (!available.length && funds.every((query) => query.isSuccess)) return null;
  return (
    <Section className="home-giving__section" aria-labelledby="quick-giving-title" flow>
      <Title level={2} id="quick-giving-title">
        {copy.quickGiving}
      </Title>
      {funds.some((query) => query.isPending) && (
        <LoadingState label={copy.loadingProjects} count={2} />
      )}
      <div className="quick-giving-grid">
        {available.map(({ kind, label, icon: Icon, fund }) => (
          <Link
            appearance="bare"
            className={`quick-giving-card quick-giving-card--${kind}`}
            key={kind}
            to={projectDonationHref(fund)}
          >
            <span className="quick-giving-card__icon">
              <Icon size={27} strokeWidth={1.6} aria-hidden="true" />
            </span>
            <span>
              <strong>{label}</strong>
              <small>{fund.publicOrganization.name}</small>
            </span>
            <ArrowRight size={20} aria-hidden="true" />
          </Link>
        ))}
      </div>
      {!available.length && funds.some((query) => query.isError) && (
        <div className="home-giving__error">
          <p role="alert">{copy.contentFailed}</p>
          <Button
            variant="ghost"
            onClick={() => {
              funds.forEach((query) => {
                void query.refetch();
              });
            }}
          >
            {copy.retry}
          </Button>
        </div>
      )}
    </Section>
  );
}

export function HomeGiving(): JSX.Element {
  const { locale } = useLanguage();
  const copy = useChurchCopy();
  const giving = useGiving();
  const client = useQueryClient();
  const hierarchy = useChurchHierarchy();
  const selected = churchPaths(hierarchy.data).find(({ church }) => church.id === giving.churchId);
  const union =
    selected?.union ?? (hierarchy.data?.unions.length === 1 ? hierarchy.data.unions[0] : undefined);
  const scope = selected?.church.id ?? union?.id;
  const recommended = useQuery({
    queryKey: publicKeys.recommended(scope, locale),
    queryFn: ({ signal }) =>
      send<Catalog>(
        `/public/organizations/${scope}/projects?recommended=true&includeAncestors=true&limit=6`,
        locale,
        { signal },
      ),
    enabled: !!scope,
    retry: 1,
  });
  useEffect(() => {
    const until = recommended.data?.recommendationsValidUntil;
    if (!until || !scope) return;
    const remaining = Date.parse(until) - Date.now() + 100;
    if (!Number.isFinite(remaining)) return;
    const timer = window.setTimeout(
      () => {
        void client.invalidateQueries({ queryKey: publicKeys.recommended(scope, locale) });
      },
      Math.min(2_147_483_647, Math.max(1000, remaining)),
    );
    return () => window.clearTimeout(timer);
  }, [client, recommended.data?.recommendationsValidUntil, scope, locale]);
  const groups = [
    ...(selected ? [{ organization: selected.conference, title: copy.conferenceProjects }] : []),
    ...(union ? [{ organization: union, title: copy.unionProjects }] : []),
  ];
  const catalogs = useQueries({
    queries: groups.map(({ organization }) => ({
      queryKey: publicKeys.homeProjects(organization.id, locale),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        send<Catalog>(`/public/organizations/${organization.id}/projects?limit=6`, locale, {
          signal,
        }),
      retry: 1,
    })),
  });
  return (
    <div className="home-giving ui-stack">
      {selected && (
        <QuickGiving
          key={selected.church.id}
          churchId={selected.church.id}
          conferenceId={selected.conference.id}
        />
      )}
      {hierarchy.isError && (
        <div className="home-giving__error">
          <p role="alert">{copy.loadFailed}</p>
          <Button
            variant="ghost"
            onClick={() => {
              void hierarchy.refetch();
            }}
          >
            {copy.retry}
          </Button>
        </div>
      )}
      {(hierarchy.isPending || (!!scope && recommended.isPending)) && (
        <LoadingState label={copy.loadingProjects} kind="cards" count={2} />
      )}
      {recommended.isError && (
        <div className="home-giving__error">
          <p role="alert">{copy.contentFailed}</p>
          <Button
            variant="ghost"
            onClick={() => {
              void recommended.refetch();
            }}
          >
            {copy.retry}
          </Button>
        </div>
      )}
      {!!recommended.data?.items.length && (
        <Section className="home-giving__section" aria-labelledby="recommended-title" flow>
          <Title level={2} id="recommended-title">
            {copy.recommended}
          </Title>
          <div className="home-projects-grid">
            {recommended.data.items.map((project) => (
              <OrganizationProjectCard key={project.id} project={project} headingLevel={3} />
            ))}
          </div>
        </Section>
      )}
      {groups.map(({ organization, title }, index) => {
        const query = catalogs[index];
        if (!query) return null;
        if (query.isSuccess && !query.data.items.length) return null;
        return (
          <Section
            className="home-giving__section"
            key={organization.id}
            aria-labelledby={`projects-${organization.id}`}
            flow
          >
            <div className="home-giving__heading">
              <div>
                <Title level={2} id={`projects-${organization.id}`}>
                  {title}
                </Title>
                <p>{organization.name}</p>
              </div>
              {(query.data?.total ?? 0) > 6 && (
                <Link to={`/o/${organization.id}`}>
                  {copy.seeAll} <ArrowRight size={16} aria-hidden="true" />
                </Link>
              )}
            </div>
            {query.isPending && (
              <LoadingState label={copy.loadingProjects} kind="cards" count={2} />
            )}
            {query.isError && (
              <div className="home-giving__error">
                <p role="alert">{copy.contentFailed}</p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void query.refetch();
                  }}
                >
                  {copy.retry}
                </Button>
              </div>
            )}
            <div className="home-projects-grid">
              {query.data?.items.map((project) => (
                <OrganizationProjectCard
                  key={project.id}
                  project={project}
                  headingLevel={3}
                  showOwner={false}
                />
              ))}
            </div>
          </Section>
        );
      })}
    </div>
  );
}
